import EmblaCarousel from '@/components/EmblaCarousel';
import { ContainerElement } from '@/containerElement';
import { Heading3Element } from '@/headingElement';
import { ImageElement } from '@/imageElement';
import { ParagraphElement } from '@/paragraphElement';
import { useMobileCarouselSettings } from '@/postElement/hooks/useMobileCarouselSettings';
import { SerializeTextLeaf } from '@/textLeaf';
import type { RSSFeedDataPodcast } from '@/types';
import { cn } from '@/utils/cn';
import type { CSSProperties, PropsWithChildren } from 'react';
import * as styles from './RSSFeed.css';
import type { RSSFeedElementProps } from './RSSFeed.types.js';
import { RSS_FEED_DEFAULTS } from './constants';
import { formatDate, getEpisodeUrl } from './util.js';

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

const OPTIONS: any = {
  align: 'start',
  containScroll: 'trimSnaps',
};
export const PodcastRssFeed = (props: PropsWithChildren<RSSFeedElementProps>) => {
  const { element, attributes, children, className, isPreviewing, rssFeedMap = {} } = props;
  const {
    isViewportMobile,
    url,
    data: rawData,
    isLoading,
    gap,
    columns,
    padding,
    itemPadding,
    itemGap,
    textGap,
    imageBorderRadius,
    titleFontSize,
    titleFontFamily,
    titleTextColor,
    titleFontWeight,
    titleAlignment,
    titleLineClamp,
    subtitleFontSize,
    subtitleFontFamily,
    subtitleTextColor,
    subtitleFontWeight,
    subtitleAlignment,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    backgroundColor,
    imageAspectRatio,
    imageFit,
    hideDate,
    isScrollHorizontally,
    isMobileScrollHorizontally,
  } = element.attrs;

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({
    attributes: {
      ...element.attrs,
      shouldScrollHorizontally: isScrollHorizontally,
      mobileShouldScrollHorizontally: isMobileScrollHorizontally,
    },
    isViewportMobile: isViewportMobile || false,
  });

  const showCount = element.attrs.showCount || 5;

  const data = rawData as RSSFeedDataPodcast;

  if (!data.rss.channel) {
    return (
      <div
        className={cn(
          'grid relative w-full h-fit',
          className,
          isLoading && 'opacity-50 animate-pulse',
          COLUMNS_MAP[columns || '3'],
        )}
        {...attributes}
      >
        <p className="text-sm text-gray-900 text-left leading-tight">Unsupported RSS feed source</p>
        <p className="text-xs text-gray-500 text-left leading-tight">{url}</p>
      </div>
    );
  }
  const desktopSlideSize =
    displayedShouldScrollHorizontally && !!effectiveColumns ? `${100 / Number.parseInt(effectiveColumns)}%` : '';

  const shownData = data.rss.channel.item.slice(0, showCount);
  return (
    <>
      <div
        className={cn(
          'relative w-full h-fit items-start',
          className,
          styles.rssFeedGrid,
          isLoading && 'opacity-50 animate-pulse',
          displayedShouldScrollHorizontally ? 'flex' : 'grid',
          !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
        )}
        style={
          {
            '--gap': gap || '16px',
            padding: padding,
            // Desktop Slider
            '--desktop-slide-size': desktopSlideSize,
            '--post-slider-control-background-color': displayedSliderControlsBackgroundColor,
            '--post-slider-control-text-color': displayedSliderControlsTextColor,
            '--post-slider-control-border-color': displayedSliderControlsBorderColor,

            // Mobile Slider
            '--mobile-slider-control-background-color':
              element.attrs.mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
            '--mobile-slider-control-text-color':
              element.attrs.mobileSliderControlsTextColor || displayedSliderControlsTextColor,
            '--mobile-slider-control-border-color':
              element.attrs.mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,
          } as CSSProperties
        }
        {...attributes}
      >
        <EmblaCarousel
          options={OPTIONS}
          isCarousel={!!displayedShouldScrollHorizontally}
          isLive
          controlsDisplayType={displayedSliderControlsDisplayType}
        >
          {shownData.map((entry) => (
            <ContainerElement
              href={entry.link}
              key={entry.guid}
              className={cn(
                'flex flex-col items-start cursor-pointer embla__slide relative h-full',
                styles.youtubeCard,
              )}
              target="_blank"
              rel="noopener noreferrer"
              element={{
                type: 'container',
                attrs: {
                  id: 'podcast-container',
                  padding: itemPadding,
                  position: 'relative',
                  backgroundColor: backgroundColor,
                  height: 'auto',
                  borderRadius: borderRadius,
                  overflow: 'hidden',
                  borderWidth: borderWidth,
                  borderColor: borderColor,
                  borderStyle: borderStyle,
                  gap: itemGap || '8px',
                },
                content: [],
              }}
              onClick={() => {
                if (!isPreviewing) {
                  const rssFeedItem = rssFeedMap?.[url];
                  const rssFeedUUID = rssFeedItem?.uuid;
                  const episodeURL = getEpisodeUrl(entry.title, rssFeedUUID, entry.guid);
                  window.open(episodeURL, '_blank');
                }
              }}
            >
              <ImageElement
                element={{
                  type: 'image',
                  attrs: {
                    src:
                      entry['itunes:image']?.['@href'] ||
                      data?.rss?.channel?.image?.url ||
                      data?.rss?.channel?.['itunes:image']?.['@href'],
                    width: '100%',
                    height: 'auto',
                    aspectRatio: imageAspectRatio || '16/9',
                    objectFit: imageFit || 'cover',
                    borderRadius: imageBorderRadius || RSS_FEED_DEFAULTS.imageBorderRadius,
                  },
                }}
              />
              <ContainerElement
                element={{
                  type: 'container',
                  attrs: {
                    id: 'container',
                    backgroundColor: 'transparent',
                    padding: '0px',
                    gap: textGap || '8px',
                  },
                  content: [],
                }}
              >
                <Heading3Element
                  element={{
                    type: 'heading',
                    attrs: {
                      id: 'heading3',
                      anchorId: 'heading3',
                      padding: '0px',
                      anchorTitle: 'heading3',
                      anchorIdSync: true,
                      anchorEnabled: true,
                      anchorTitleSync: true,
                      customAttributes: {},
                      textAlign: titleAlignment || 'left',
                      level: 3,
                    },
                    content: [
                      {
                        text: entry.title,
                      },
                    ],
                  }}
                  className={titleLineClamp ? `${titleLineClamp} text-ellipsis` : ''}
                >
                  <SerializeTextLeaf
                    leaf={{
                      text: entry.title,
                      marks: [
                        {
                          type: 'textStyle',
                          attrs: {
                            fontSize: titleFontSize || RSS_FEED_DEFAULTS.titleFontSize,
                            fontFamily: titleFontFamily || RSS_FEED_DEFAULTS.titleFontFamily,
                            color: titleTextColor || RSS_FEED_DEFAULTS.titleTextColor,
                            fontWeight: titleFontWeight || RSS_FEED_DEFAULTS.titleFontWeight,
                          },
                        },
                      ],
                    }}
                  />
                </Heading3Element>
                {!hideDate && (
                  <ParagraphElement
                    element={{
                      type: 'paragraph',
                      attrs: {
                        textAlign: subtitleAlignment || 'left',
                        padding: '0px',
                      },
                    }}
                  >
                    <SerializeTextLeaf
                      leaf={{
                        text: formatDate(entry.pubDate),
                        marks: [
                          {
                            type: 'textStyle',
                            attrs: {
                              fontSize: subtitleFontSize || RSS_FEED_DEFAULTS.subtitleFontSize,
                              fontFamily: subtitleFontFamily || RSS_FEED_DEFAULTS.subtitleFontFamily,
                              color: subtitleTextColor || RSS_FEED_DEFAULTS.subtitleTextColor,
                              fontWeight: subtitleFontWeight || RSS_FEED_DEFAULTS.subtitleFontWeight,
                            },
                          },
                        ],
                      }}
                    />
                  </ParagraphElement>
                )}
              </ContainerElement>
            </ContainerElement>
          ))}
        </EmblaCarousel>
        {children}
      </div>
    </>
  );
};
